import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, IndianRupee, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminRevenue = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ transactions: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/transactions`, { withCredentials: true });
      setData(data);
    } catch { toast.error('Failed to load transactions'); }
    finally { setLoading(false); }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.transactions || []).filter((t) => {
      if (methodFilter === 'cod' && t.payment_method !== 'cod') return false;
      if (methodFilter === 'prepaid' && t.payment_method === 'cod') return false;
      if (!q) return true;
      return (
        (t.party_name || '').toLowerCase().includes(q) ||
        (t.email || '').toLowerCase().includes(q) ||
        (t.transaction_id || '').toLowerCase().includes(q) ||
        (t.order_id || '').toLowerCase().includes(q)
      );
    });
  }, [data, search, methodFilter]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' IST';
  };

  const StatTile = ({ label, value, accent }) => (
    <div className="p-4 rounded-xl bg-white border border-[#E0E0E0]">
      <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-xl font-black ${accent || ''}`}>{'\u20B9'}{(value || 0).toFixed(0)}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => navigate('/admin')} data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <h1 className="text-3xl font-black font-heading flex items-center gap-2" data-testid="admin-revenue-heading">
              <IndianRupee className="h-7 w-7 text-primary" /> Revenue
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatTile label="Total Revenue" value={data.totals?.all} accent="text-primary" />
          <StatTile label="Prepaid" value={data.totals?.prepaid} />
          <StatTile label="COD Received" value={data.totals?.cod_received} accent="text-green-700" />
          <StatTile label="COD Pending" value={data.totals?.cod_pending} accent="text-yellow-700" />
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by party, email, transaction ID, order ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="revenue-search-input"
            />
          </div>
          <select
            className="border border-[#E0E0E0] rounded-md px-3 py-2 text-sm bg-white"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            data-testid="revenue-method-filter"
          >
            <option value="all">All payment methods</option>
            <option value="prepaid">Prepaid (Razorpay)</option>
            <option value="cod">Cash on Delivery</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-[#E0E0E0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="revenue-table">
              <thead className="bg-[#FAFAFA] border-b border-[#E0E0E0]">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Party</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Transaction ID</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">COD Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No transactions found.</td></tr>
                ) : rows.map((t) => (
                  <tr key={t.order_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`revenue-row-${t.order_id}`}>
                    <td className="px-4 py-3 font-medium">
                      {t.party_name || '—'}
                      {t.guest && <span className="ml-2 text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">Guest</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{t.email || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{t.transaction_id || '—'}</td>
                    <td className="px-4 py-3 text-right font-bold">{'\u20B9'}{(t.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full ${t.payment_method === 'cod' ? 'bg-yellow-50 text-yellow-800' : 'bg-blue-50 text-blue-800'}`}>
                        {t.payment_method || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {t.payment_method === 'cod' ? (
                        <span className={`text-[11px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full ${t.cod_status === 'received' ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>
                          {t.cod_status}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-3">Showing {rows.length} of {(data.transactions || []).length} transactions.</p>
      </div>
    </div>
  );
};

export default AdminRevenue;
